import { CarIcon, CartIcon, PinLocationIcon } from '@/components/Icons';
import { timelineIcons } from '@/components/Icons/TimelineIcons';
import { DateRangeDirection, EnableTimelineLayout, HighlightContentPreviewId, TimelineIconStages } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { calculateEstimatedDays, getProcessedDate } from '@/helpers/time';
import useClickHighlight from '@/hooks/useClickHighlight';
import { useHasWorkDay } from '@/hooks/useHasWorkDay';
import highlightPreviewSlice, { highlightPreviewSelector } from '@/redux/slice/highlightPreview.slice';
import settingsSlice, { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { ILanguageRule } from '@/types/languageRule.type';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ShippingFlowStyled } from './styled';
interface IProps {
  orderToShipDays: number;
  prepDays: number;
  shipToReceiveDays: number;
  estDays: number;
  languageRule?: ILanguageRule;
  preOrderDate?: string;
  isUsePreOrderDate?: boolean;
}

const ShippingFlow = ({ ...props }: IProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const dataSettings = useSelector(dataSettingsSelector);
  const { handleClickHighlight } = useClickHighlight();
  const {
    isHighlightedOrderIcon,
    isHighlightedShipIcon,
    isHighlightedDeliveryIcon,
    isHighlightedOrderTitle,
    isHighlightedShipTitle,
    isHighlightedDeliveryTitle,
    isHighlightedOrderTooltip,
    isHighlightedShipTooltip,
    isHighlightedDeliveryTooltip,
    isHighlightedDateRangeIcon,
  } = useSelector(highlightPreviewSelector);
  const { isHasWorkDay } = useHasWorkDay();

  const { startShippedDay, endShippedDay, startDeliveryDay, endDeliveryDay } = calculateEstimatedDays(
    props.orderToShipDays,
    props.prepDays,
    props.shipToReceiveDays,
    props.estDays,
    props?.isUsePreOrderDate && props.preOrderDate ? new Date(props.preOrderDate) : undefined,
  );

  const orderedDate = getProcessedDate(new Date());
  const startShipped = getProcessedDate(startShippedDay);
  const endShipped = getProcessedDate(endShippedDay);
  const startDelivered = getProcessedDate(startDeliveryDay);
  const endDelivered = getProcessedDate(endDeliveryDay);

  const {
    timelineOrderStepTitle,
    timelineShipStepTitle,
    timelineDeliveryStepTitle,
    timelineOrderStepDescription,
    timelineShipStepDescription,
    timelineDeliveryStepDescription,
  } = useMemo(() => {
    const timelineOrderStepTitle =
      props.languageRule?.appearanceContent?.timelineOrderStepTitle || dataSettings.timeline_order_step_title;
    const timelineShipStepTitle =
      props.languageRule?.appearanceContent?.timelineShipStepTitle || dataSettings.timeline_ship_step_title;
    const timelineDeliveryStepTitle =
      props.languageRule?.appearanceContent?.timelineDeliveryStepTitle || dataSettings.timeline_delivery_step_title;
    const timelineOrderStepDescription =
      props.languageRule?.appearanceContent?.timelineOrderStepDescription || dataSettings.timeline_order_step_description;
    const timelineShipStepDescription =
      props.languageRule?.appearanceContent?.timelineShipStepDescription || dataSettings.timeline_ship_step_description;
    const timelineDeliveryStepDescription =
      props.languageRule?.appearanceContent?.timelineDeliveryStepDescription || dataSettings.timeline_delivery_step_description;
    return {
      timelineOrderStepTitle,
      timelineShipStepTitle,
      timelineDeliveryStepTitle,
      timelineOrderStepDescription,
      timelineShipStepDescription,
      timelineDeliveryStepDescription,
    };
  }, [
    dataSettings.timeline_delivery_step_description,
    dataSettings.timeline_delivery_step_title,
    dataSettings.timeline_order_step_description,
    dataSettings.timeline_order_step_title,
    dataSettings.timeline_ship_step_description,
    dataSettings.timeline_ship_step_title,
    props.languageRule?.appearanceContent?.timelineDeliveryStepDescription,
    props.languageRule?.appearanceContent?.timelineDeliveryStepTitle,
    props.languageRule?.appearanceContent?.timelineOrderStepDescription,
    props.languageRule?.appearanceContent?.timelineOrderStepTitle,
    props.languageRule?.appearanceContent?.timelineShipStepDescription,
    props.languageRule?.appearanceContent?.timelineShipStepTitle,
  ]);

  // This works because the unary + operator converts true to 1 and false to 0.
  const shippingPointCount: number =
    +dataSettings.timeline_delivery_step_enable +
    +dataSettings.timeline_order_step_enable +
    +dataSettings.timeline_ship_step_enable;

  const timelineLayoutItemWidthPercentage = 100 / shippingPointCount;

  useEffect(() => {
    if (shippingPointCount === 0) {
      dispatch(
        settingsSlice.actions.handleDataSettings({ ...dataSettings, enable_timeline_layout: EnableTimelineLayout.InActive }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingPointCount]);

  const handleFilterIcon = (stage: TimelineIconStages) => {
    if (dataSettings[stage]) {
      if (dataSettings[stage]?.isCustom) {
        return (
          <img
            className="timeline-layout--svg--icon"
            src={dataSettings[stage]?.iconUrl}
            alt="custom icon"
            width={30}
            height={30}
            style={{ objectFit: 'cover', objectPosition: 'center' }}
          />
        );
      }
      let icon = timelineIcons
        .map((item) => item.icons)
        .flat()
        .find((item) => +item.id === dataSettings[stage]?.iconId)?.icon;
      if (icon) {
        icon = React.cloneElement(icon, { fill: dataSettings.icon_color });
      }
      return icon;
    }
    switch (stage) {
      case TimelineIconStages.Stage1: {
        return <CartIcon />;
      }
      case TimelineIconStages.Stage2: {
        return <CarIcon />;
      }
      case TimelineIconStages.Stage3: {
        return <PinLocationIcon />;
      }
      default:
        break;
    }
  };

  const handleClickIcon = (stage: TimelineIconStages) => {
    if (location.pathname !== PATH.APPEARANCE.pathname) return;
    const key =
      stage === TimelineIconStages.Stage1
        ? 'isOrderIconClicked'
        : stage === TimelineIconStages.Stage2
        ? 'isShipIconClicked'
        : 'isDeliveryIconClicked';
    dispatch(highlightPreviewSlice.actions.handleClickHighlightItem({ key, value: true }));
  };

  const orderStep = dataSettings.timeline_order_step_enable ? (
    <span
      className={`timeline-layout--item ${shippingPointCount === 2 ? 'two-item' : ''} ${
        shippingPointCount === 1 ? '' : 'with-connect-line'
      }`}
      style={{ width: `${timelineLayoutItemWidthPercentage}%` }}
    >
      <div className="timeline-layout--item--wrapper">
        <div
          className={`timeline-layout--svg ${isHighlightedOrderIcon ? 'item-highlight-outline' : ''}`}
          onClick={() => handleClickIcon(TimelineIconStages.Stage1)}
          style={{ cursor: 'pointer' }}
        >
          {handleFilterIcon(TimelineIconStages.Stage1)}
        </div>
        <br />
        <span className="timeline-layout--text">
          <label
            className={`${isHighlightedOrderTitle ? 'item-highlight-outline' : ''}`}
            htmlFor={HighlightContentPreviewId.TimelineOrderStepTitle}
            style={{ position: 'relative', zIndex: 1 }}
          >
            <b>
              <span className={`timeline-layout--title ${timelineOrderStepDescription ? 'dot-underline' : ''}`}>
                {timelineOrderStepTitle}
                <span
                  className={`${timelineOrderStepDescription ? 'timeline-layout--description' : 'ot-hide'}`}
                  style={{
                    visibility: isHighlightedOrderTooltip ? 'visible' : 'hidden',
                  }}
                >
                  <label htmlFor={HighlightContentPreviewId.TimelineOrderStepDescription}>{timelineOrderStepDescription}</label>
                </span>
              </span>
            </b>
          </label>
          <br />
          <span onClick={handleClickHighlight('isHighlightedDateAppearance')}>{orderedDate}</span>
        </span>
      </div>
    </span>
  ) : null;

  let shipStepDateRange = (
    <div
      className={`ot-estimated-shipping-timeline-range ${
        dataSettings.timeline_date_range_direction === DateRangeDirection.Horizontal ? 'horizontal' : 'vertical'
      }`}
    >
      <span
        onClick={handleClickHighlight('isHighlightedDateAppearance')}
        className="ot-estimated-shipping-timeline-range-start-shipped"
      >
        {startShipped}
      </span>{' '}
      <span className={`ot-estimated-shipping-timeline-range-shipped-down`}>
        <label
          htmlFor={HighlightContentPreviewId.DateRangeIconSetup}
          className={`${isHighlightedDateRangeIcon ? 'item-highlight-outline' : ''}`}
        >
          {dataSettings.timeline_date_range_icon}
        </label>
      </span>{' '}
      <span
        onClick={handleClickHighlight('isHighlightedDateAppearance')}
        className="ot-estimated-shipping-timeline-range-end-shipped"
      >
        {endShipped}
      </span>
    </div>
  );
  if (startShipped === endShipped) {
    shipStepDateRange = <span onClick={handleClickHighlight('isHighlightedDateAppearance')}>{endShipped}</span>;
  }
  const shipStep = dataSettings.timeline_ship_step_enable ? (
    <span
      className={`timeline-layout--item ${shippingPointCount === 2 ? 'two-item' : ''} ${
        shippingPointCount === 1 ? '' : 'with-connect-line'
      }`}
      style={{ width: `${timelineLayoutItemWidthPercentage}%` }}
    >
      <div
        className={`timeline-layout--svg ${isHighlightedShipIcon ? 'item-highlight-outline' : ''}`}
        onClick={() => handleClickIcon(TimelineIconStages.Stage2)}
        style={{ cursor: 'pointer' }}
      >
        {handleFilterIcon(TimelineIconStages.Stage2)}
      </div>
      <br />
      <span className="timeline-layout--text">
        <label
          className={`${isHighlightedShipTitle ? 'item-highlight-outline' : ''}`}
          htmlFor={HighlightContentPreviewId.TimelineShipStepTitle}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <b>
            <span className={`timeline-layout--title ${timelineShipStepDescription ? 'dot-underline' : ''}`}>
              {timelineShipStepTitle}
              <span
                className={`${timelineShipStepDescription ? 'timeline-layout--description' : 'ot-hide'}`}
                style={{
                  visibility: isHighlightedShipTooltip ? 'visible' : 'hidden',
                }}
              >
                <label htmlFor={HighlightContentPreviewId.TimelineShipStepDescription}>{timelineShipStepDescription}</label>
              </span>
            </span>
            <br />
          </b>
        </label>
        <div>{shipStepDateRange}</div>
      </span>
    </span>
  ) : null;
  let deliveryStepDateRange = (
    <div
      className={`ot-estimated-shipping-timeline-range ${
        dataSettings.timeline_date_range_direction === DateRangeDirection.Horizontal ? 'horizontal' : 'vertical'
      }`}
    >
      <span
        onClick={handleClickHighlight('isHighlightedDateAppearance')}
        className="ot-estimated-shipping-timeline-range-start-delivery"
      >
        {startDelivered}
      </span>{' '}
      <span className={`ot-estimated-shipping-timeline-range-delivery-down`}>
        <label
          htmlFor={HighlightContentPreviewId.DateRangeIconSetup}
          className={`${isHighlightedDateRangeIcon ? 'item-highlight-outline' : ''}`}
        >
          {dataSettings.timeline_date_range_icon}
        </label>
      </span>{' '}
      <span
        onClick={handleClickHighlight('isHighlightedDateAppearance')}
        className="ot-estimated-shipping-timeline-range-end-delivery"
      >
        {endDelivered}
      </span>
    </div>
  );
  if (startDelivered === endDelivered) {
    deliveryStepDateRange = <span onClick={handleClickHighlight('isHighlightedDateAppearance')}>{endDelivered}</span>;
  }
  const deliveryStep = dataSettings.timeline_delivery_step_enable ? (
    <span className="timeline-layout--item" style={{ width: `${timelineLayoutItemWidthPercentage}%` }}>
      <div className="timeline-layout--item--wrapper">
        <div
          className={`timeline-layout--svg ${isHighlightedDeliveryIcon ? 'item-highlight-outline' : ''}`}
          onClick={() => handleClickIcon(TimelineIconStages.Stage3)}
          style={{ cursor: 'pointer' }}
        >
          {handleFilterIcon(TimelineIconStages.Stage3)}
        </div>
        <br />
        <span className="timeline-layout--text">
          <label
            className={`${isHighlightedDeliveryTitle ? 'item-highlight-outline' : ''}`}
            htmlFor={HighlightContentPreviewId.TimelineDeliveryStepTitle}
            style={{ position: 'relative', zIndex: 1, display: 'block' }}
          >
            <b>
              <span className={`timeline-layout--title ${timelineDeliveryStepDescription ? 'dot-underline' : ''}`}>
                {timelineDeliveryStepTitle}
                <span
                  className={`${timelineDeliveryStepDescription ? 'timeline-layout--description last-description' : 'ot-hide'}`}
                  style={{
                    visibility: isHighlightedDeliveryTooltip ? 'visible' : 'hidden',
                  }}
                >
                  <label htmlFor={HighlightContentPreviewId.TimelineDeliveryStepDescription}>
                    {timelineDeliveryStepDescription}
                  </label>
                </span>
              </span>
              <br />
            </b>
          </label>
          <span>{deliveryStepDateRange}</span>
        </span>
      </div>
    </span>
  ) : null;

  const content = (
    <span className="timeline-layout">
      {/* <!-- Order Step --> */}
      {orderStep}
      {/* <!-- Shipped Step --> */}
      {shipStep}
      {/* <!-- Delivered Step --> */}
      {deliveryStep}
    </span>
  );
  return isHasWorkDay && dataSettings.enable_timeline_layout && shippingPointCount !== 0 ? (
    <ShippingFlowStyled className="ot-estimated-shipping-timeline-layout" generalSetting={dataSettings}>
      {content}
    </ShippingFlowStyled>
  ) : null;
};

export default ShippingFlow;
